import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Copy, FlaskConical, History, Loader2, Maximize2, Save, Trash2 } from 'lucide-react';
import type { AdminCatalog, AdminPreset, PresetFields, ReferenceImage } from '../../api/imagePresets';
import { presetError } from '../../api/imagePresets';
import { useCreatePreset, useDeletePreset, useReferencePreview, useUpdatePreset } from '../../hooks';
import { getErrorDetail } from '../../utils/error';
import { AspectPicker, FieldMessage, Toggle } from './common';
import { CHECKERBOARD, fieldErrors, formatKst, toFields } from './format';
import ReferenceViewer from './ReferenceViewer';

const EDITABLE: (keyof PresetFields)[] = [
  'name',
  'category',
  'description',
  'prompt',
  'negative_prompt',
  'variables',
  'allow_text_in_image',
  'aspect',
  'reference_images',
  'provider',
  'model',
  'quality',
  'enabled',
];

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * 부모는 프리셋·버전마다 key 를 바꿔 이 컴포넌트를 새로 그린다 — 다른 프리셋을 고르거나
 * 저장 뒤 새 버전이 오면 편집 상태(초안·메모·오류)가 처음부터 시작한다.
 */
interface Props {
  /** 고치는 중인 저장본. null 이면 새로 만든다. */
  preset: AdminPreset | null;
  /** 새로 만들 때의 시작 내용(복제 포함). */
  initial: PresetFields;
  catalog: AdminCatalog;
  canEdit: boolean;
  onSaved: (preset: AdminPreset) => void;
  onDeleted: () => void;
  onDuplicate: (draft: PresetFields) => void;
  onOpenHistory: () => void;
  onOpenTrial: (draft: PresetFields) => void;
}

/** 고르는 칩 안의 작은 그림. */
const ReferenceThumb: React.FC<{ image: ReferenceImage }> = ({ image }) => {
  const url = useReferencePreview(image.name, image.sha256);
  return (
    <span className={`${CHECKERBOARD} h-6 w-6 shrink-0 rounded-full overflow-hidden flex items-center justify-center`}>
      {url && <img src={url} alt="" className="h-full w-full object-cover" />}
    </span>
  );
};

const PresetEditor: React.FC<Props> = ({
  preset,
  initial,
  catalog,
  canEdit,
  onSaved,
  onDeleted,
  onDuplicate,
  onOpenHistory,
  onOpenTrial,
}) => {
  const { t } = useTranslation();
  const isNew = preset === null;
  const [draft, setDraft] = useState<PresetFields>(initial);
  const [note, setNote] = useState('');
  const [variablesText, setVariablesText] = useState(initial.variables.join(', '));
  const create = useCreatePreset();
  const update = useUpdatePreset();
  const remove = useDeletePreset();
  const [conflict, setConflict] = useState<AdminPreset | null>(null);
  const [viewing, setViewing] = useState<ReferenceImage | null>(null);

  const base = useMemo(() => (preset ? toFields(preset) : null), [preset]);
  const changes = useMemo(() => {
    if (!base) return draft;
    return Object.fromEntries(EDITABLE.filter((k) => !same(draft[k], base[k])).map((k) => [k, draft[k]]));
  }, [draft, base]);
  const dirty = isNew || Object.keys(changes).length > 0;

  const saveError = presetError(create.error ?? update.error);
  const errors = fieldErrors(saveError?.errors);
  const saving = create.isPending || update.isPending;

  const set = <K extends keyof PresetFields>(key: K, value: PresetFields[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    try {
      const saved = isNew
        ? await create.mutateAsync({ fields: draft, note: note || undefined })
        : await update.mutateAsync({ id: draft.id, version: preset.version, changes, note: note || undefined });
      onSaved(saved);
    } catch (e) {
      const body = presetError(e);
      if (body?.error_code === 'IMAGE_PRESET_VERSION_CONFLICT' && body.current) setConflict(body.current);
    }
  };

  const del = async () => {
    if (!preset || !window.confirm(t('imagePresets.confirmDelete', { id: preset.id }))) return;
    try {
      await remove.mutateAsync({ id: preset.id, version: preset.version });
      onDeleted();
    } catch (e) {
      const body = presetError(e);
      if (body?.current) setConflict(body.current);
    }
  };

  const label = (key: string, hint?: string) => (
    <label htmlFor={`pf-${key}`} className="block text-sm font-medium text-slate-700 mb-1">
      {t(`imagePresets.field.${key}`)}
      {hint && <span className="ml-2 font-normal text-xs text-slate-400">{hint}</span>}
    </label>
  );
  const ro = !canEdit;

  return (
    <div className="card flex flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-slate-800 truncate">
            {isNew ? t('imagePresets.newPreset') : preset.name}
          </h3>
          {!isNew && (
            <p className="text-xs text-slate-500 mt-0.5">
              <span className="font-mono">{preset.id}</span> · v{preset.version} · {formatKst(preset.updated_at)}
              {preset.updated_by && ` · ${preset.updated_by}`}
            </p>
          )}
        </div>
        {!isNew && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onOpenHistory}
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
            >
              <History size={15} /> {t('imagePresets.history')}
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={() => onDuplicate({ ...draft, id: `${draft.id}-copy`, enabled: false })}
                className="btn-secondary text-sm inline-flex items-center gap-1.5"
              >
                <Copy size={15} /> {t('imagePresets.duplicate')}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="px-5 py-4 space-y-5">
        {conflict && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm flex gap-3" role="alert">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-amber-900">
                {t('imagePresets.conflict', { by: conflict.updated_by ?? '-', version: conflict.version })}
              </p>
              <button type="button" className="btn-secondary text-xs bg-white" onClick={() => onSaved(conflict)}>
                {t('imagePresets.loadLatest')}
              </button>
            </div>
          </div>
        )}
        {errors['']?.length ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
            {errors[''].join(' · ')}
          </div>
        ) : null}
        {saveError && !saveError.errors && !conflict && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
            {getErrorDetail(create.error ?? update.error, t('imagePresets.saveFailed'))}
          </div>
        )}

        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
          <div>
            <p className="text-sm font-medium text-slate-700">{t('imagePresets.field.enabled')}</p>
            <p className="text-xs text-slate-500">{t('imagePresets.enabledHint')}</p>
          </div>
          <Toggle
            checked={draft.enabled}
            onChange={(v) => set('enabled', v)}
            disabled={ro}
            label={t('imagePresets.field.enabled')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            {label('name')}
            <input
              id="pf-name"
              className="input"
              value={draft.name}
              onChange={(e) => set('name', e.target.value)}
              disabled={ro}
            />
            <FieldMessage messages={errors.name} />
          </div>
          <div>
            {label('id', isNew ? t('imagePresets.idHint') : t('imagePresets.idLocked'))}
            <input
              id="pf-id"
              className="input font-mono disabled:bg-slate-50 disabled:text-slate-500"
              value={draft.id}
              onChange={(e) => set('id', e.target.value)}
              disabled={!isNew || ro}
            />
            <FieldMessage messages={errors.id} />
          </div>
          <div>
            {label('category')}
            <select
              id="pf-category"
              className="input bg-white"
              value={draft.category}
              onChange={(e) => set('category', e.target.value)}
              disabled={ro}
            >
              {catalog.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.id})
                </option>
              ))}
            </select>
            <FieldMessage messages={errors.category} />
          </div>
          <div>
            {label('description')}
            <input
              id="pf-description"
              className="input"
              value={draft.description}
              onChange={(e) => set('description', e.target.value)}
              disabled={ro}
            />
          </div>
        </div>

        <div>
          {label('aspect', t('imagePresets.aspectHint'))}
          <AspectPicker value={draft.aspect} onChange={(a) => set('aspect', a)} disabled={ro} />
          <FieldMessage messages={errors.aspect} />
        </div>

        <div>
          {label('prompt', t('imagePresets.promptHint'))}
          <textarea
            id="pf-prompt"
            className="input font-mono text-[13px] leading-relaxed min-h-[16rem] resize-y"
            value={draft.prompt}
            onChange={(e) => set('prompt', e.target.value)}
            disabled={ro}
            spellCheck={false}
          />
          <FieldMessage messages={errors.prompt} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            {label('negative_prompt', t('imagePresets.negativeHint'))}
            <textarea
              id="pf-negative_prompt"
              className="input font-mono text-[13px] min-h-[5rem] resize-y"
              value={draft.negative_prompt ?? ''}
              onChange={(e) => set('negative_prompt', e.target.value || null)}
              disabled={ro}
            />
          </div>
          <div className="space-y-4">
            <div>
              {label('variables', t('imagePresets.variablesHint'))}
              <input
                id="pf-variables"
                className="input"
                value={variablesText}
                onChange={(e) => {
                  setVariablesText(e.target.value);
                  set(
                    'variables',
                    e.target.value
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean),
                  );
                }}
                disabled={ro}
              />
              <FieldMessage messages={errors.variables} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.allow_text_in_image}
                onChange={(e) => set('allow_text_in_image', e.target.checked)}
                disabled={ro}
              />
              {t('imagePresets.field.allow_text_in_image')}
            </label>
          </div>
        </div>

        <div>
          {label('reference_images', t('imagePresets.referenceHint'))}
          {catalog.reference_images.length === 0 ? (
            <p className="text-sm text-slate-500">{t('imagePresets.noReferences')}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {catalog.reference_images.map((ref) => {
                const index = draft.reference_images.indexOf(ref.name);
                const on = index >= 0;
                return (
                  <div
                    key={ref.name}
                    className={`inline-flex items-stretch rounded-full border overflow-hidden ${
                      on ? 'border-blue-600 bg-blue-50' : 'border-slate-200'
                    }`}
                  >
                    <button
                      type="button"
                      disabled={ro}
                      aria-pressed={on}
                      onClick={() =>
                        set(
                          'reference_images',
                          on
                            ? draft.reference_images.filter((n) => n !== ref.name)
                            : [...draft.reference_images, ref.name],
                        )
                      }
                      className={`inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 text-xs font-mono transition-colors ${
                        on ? 'text-blue-700' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <ReferenceThumb image={ref} />
                      {/* 사진 뒤에 이 순서로 붙는다 — 프롬프트가 IMAGE 2, 3 으로 가리키는 번호. */}
                      {on && <span className="font-sans font-semibold">IMAGE {index + 2}</span>}
                      {ref.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewing(ref)}
                      title={t('imagePresets.viewReference')}
                      aria-label={t('imagePresets.viewReference')}
                      className={`px-2 border-l text-slate-500 hover:bg-slate-100 hover:text-slate-800 ${
                        on ? 'border-blue-200' : 'border-slate-200'
                      }`}
                    >
                      <Maximize2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {draft.reference_images
            .filter((n) => !catalog.reference_images.some((r) => r.name === n))
            .map((missing) => (
              <p key={missing} className="mt-1 text-xs text-red-600">
                {t('imagePresets.missingReference', { name: missing })}{' '}
                {canEdit && (
                  <button
                    type="button"
                    className="underline"
                    onClick={() =>
                      set(
                        'reference_images',
                        draft.reference_images.filter((n) => n !== missing),
                      )
                    }
                  >
                    {t('imagePresets.removeFromList')}
                  </button>
                )}
              </p>
            ))}
          <FieldMessage messages={errors.reference_images} />
          {viewing && <ReferenceViewer image={viewing} onClose={() => setViewing(null)} />}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            {label('provider')}
            <select
              id="pf-provider"
              className="input bg-white"
              value={draft.provider}
              onChange={(e) => set('provider', e.target.value as PresetFields['provider'])}
              disabled={ro}
            >
              <option value="openai">openai</option>
              <option value="gemini">gemini</option>
            </select>
          </div>
          <div>
            {label('model')}
            <input
              id="pf-model"
              className="input font-mono"
              value={draft.model}
              onChange={(e) => set('model', e.target.value)}
              disabled={ro}
            />
            <FieldMessage messages={errors.model} />
          </div>
          <div>
            {label('quality', t('imagePresets.qualityHint'))}
            <input
              id="pf-quality"
              className="input font-mono"
              value={draft.quality ?? ''}
              onChange={(e) => set('quality', e.target.value || null)}
              disabled={ro}
              list="pf-quality-options"
            />
            <datalist id="pf-quality-options">
              {['low', 'medium', 'high', 'auto'].map((q) => (
                <option key={q} value={q} />
              ))}
            </datalist>
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-slate-200 bg-white/95 backdrop-blur px-5 py-3 rounded-b-xl">
          <input
            className="input flex-1 min-w-[10rem] text-sm"
            placeholder={t('imagePresets.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label={t('imagePresets.notePlaceholder')}
          />
          <button
            type="button"
            onClick={() => onOpenTrial(draft)}
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
          >
            <FlaskConical size={15} /> {t('imagePresets.trial')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="btn-primary text-sm inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {isNew ? t('imagePresets.create') : dirty ? t('imagePresets.save') : t('imagePresets.saved')}
          </button>
          {!isNew && (
            <button
              type="button"
              onClick={del}
              disabled={remove.isPending}
              className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
              aria-label={t('imagePresets.delete')}
              title={t('imagePresets.delete')}
            >
              <Trash2 size={17} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PresetEditor;
